/* eslint-disable react/no-multi-comp -- техдолг: вспомогательные компоненты вынести в отдельные файлы (docs/todos/tech-debt/admin-page-helper-components.md) */
import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { useListAdminCafeStaffSearchLogsQuery } from '@/api/cafeStaffSearchLogsApi';
import { Input } from '@/components/Input';

const PAGE_SIZE = 20;

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

interface FiltersBlockProps {
    filters: Record<string, unknown>;
    rowId: string;
}

function FiltersBlock({ filters, rowId }: FiltersBlockProps): ReactElement {
    const [isOpen, setOpen] = useState(false);
    const isEmpty = Object.keys(filters).length === 0;

    if (isEmpty) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    return (
        <div className="w-full" data-testid={`cafe-search-log-filters-${rowId}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`cafe-search-log-filters-toggle-${rowId}`}
                className="inline-flex items-center gap-1 text-xs text-gray-700 underline transition hover:text-gray-900"
            >
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {isOpen ? 'Скрыть' : `Показать (${Object.keys(filters).length})`}
            </button>
            {isOpen && (
                <pre
                    className="mt-1 max-h-64 overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-800"
                    data-testid={`cafe-search-log-filters-json-${rowId}`}
                >
                    {JSON.stringify(filters, null, 2)}
                </pre>
            )}
        </div>
    );
}

export function CafeStaffSearchLogsPage(): ReactElement {
    const [filterCafeId, setFilterCafeId] = useState('');
    const [filterPerformedBy, setFilterPerformedBy] = useState('');
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');

    const [appliedCafeId, setAppliedCafeId] = useState('');
    const [appliedPerformedBy, setAppliedPerformedBy] = useState('');
    const [appliedFrom, setAppliedFrom] = useState('');
    const [appliedTo, setAppliedTo] = useState('');

    const [page, setPage] = useState(0);

    const queryArg = useMemo(
        () => ({
            ...(appliedCafeId !== '' ? { cafeId: appliedCafeId } : {}),
            ...(appliedPerformedBy !== ''
                ? { performedByUserId: appliedPerformedBy }
                : {}),
            ...(appliedFrom !== '' ? { from: appliedFrom } : {}),
            ...(appliedTo !== '' ? { to: appliedTo } : {}),
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
        }),
        [appliedCafeId, appliedPerformedBy, appliedFrom, appliedTo, page]
    );

    const { data, isLoading, isError } = useListAdminCafeStaffSearchLogsQuery(queryArg);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

    const handleApplyFilters = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setAppliedCafeId(filterCafeId.trim());
        setAppliedPerformedBy(filterPerformedBy.trim());
        setAppliedFrom(filterFrom !== '' ? new Date(filterFrom).toISOString() : '');
        setAppliedTo(filterTo !== '' ? new Date(filterTo).toISOString() : '');
        setPage(0);
    };

    const handleResetFilters = (): void => {
        setFilterCafeId('');
        setFilterPerformedBy('');
        setFilterFrom('');
        setFilterTo('');
        setAppliedCafeId('');
        setAppliedPerformedBy('');
        setAppliedFrom('');
        setAppliedTo('');
        setPage(0);
    };

    return (
        <div data-testid="cafe-staff-search-logs-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">
                    Audit-log поиска кафе
                </h1>
            </div>

            <form
                onSubmit={handleApplyFilters}
                className="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4"
                data-testid="cafe-staff-search-logs-filters"
            >
                <Input
                    label="Cafe ID"
                    value={filterCafeId}
                    onChange={(e) => setFilterCafeId(e.target.value)}
                    placeholder="UUID кафе"
                    data-testid="cafe-staff-search-logs-filter-cafe"
                />
                <Input
                    label="Кто искал (User ID)"
                    value={filterPerformedBy}
                    onChange={(e) => setFilterPerformedBy(e.target.value)}
                    placeholder="UUID пользователя"
                    data-testid="cafe-staff-search-logs-filter-user"
                />
                <Input
                    label="С даты"
                    type="datetime-local"
                    value={filterFrom}
                    onChange={(e) => setFilterFrom(e.target.value)}
                    data-testid="cafe-staff-search-logs-filter-from"
                />
                <Input
                    label="По дату"
                    type="datetime-local"
                    value={filterTo}
                    onChange={(e) => setFilterTo(e.target.value)}
                    data-testid="cafe-staff-search-logs-filter-to"
                />
                <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
                    <button
                        type="submit"
                        className="rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                        data-testid="cafe-staff-search-logs-filter-submit"
                    >
                        Применить
                    </button>
                    <button
                        type="button"
                        onClick={handleResetFilters}
                        className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        data-testid="cafe-staff-search-logs-filter-reset"
                    >
                        Сбросить
                    </button>
                </div>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div
                    className="text-sm text-red-600"
                    data-testid="cafe-staff-search-logs-error"
                >
                    Не удалось загрузить журнал
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="cafe-staff-search-logs-table"
                >
                    <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_80px_minmax(0,2fr)] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>Когда</span>
                        <span>Cafe ID</span>
                        <span>Performed by</span>
                        <span>Результаты</span>
                        <span>Фильтры</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Записи не найдены
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={item.id}
                            className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_80px_minmax(0,2fr)] items-start gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`cafe-staff-search-logs-row-${item.id}`}
                        >
                            <span className="text-xs text-gray-700">
                                {formatDateTime(item.performedAt)}
                            </span>
                            <span
                                className="truncate font-mono text-xs text-gray-800"
                                title={item.cafeId}
                            >
                                {item.cafeId}
                            </span>
                            <span
                                className="truncate font-mono text-xs text-gray-700"
                                title={item.performedByUserId ?? ''}
                            >
                                {item.performedByUserId ?? '—'}
                            </span>
                            <span className="text-gray-900">{item.resultsCount}</span>
                            <FiltersBlock filters={item.filters} rowId={item.id} />
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div
                    className="flex items-center justify-between text-sm text-gray-700"
                    data-testid="cafe-staff-search-logs-pagination"
                >
                    <span>
                        Страница {page + 1} из {totalPages} · всего {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="cafe-staff-search-logs-page-prev"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= totalPages}
                            data-testid="cafe-staff-search-logs-page-next"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Вперёд
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
